// Translates the rendered interface. Pages keep their English copy; this layer swaps the text
// that reaches the screen using src/i18n/locales/<language>.json, which is keyed by that English
// copy (see scripts/extract-ui-strings.mjs). Text marked translate="no" is left alone.
import type { AtlasLanguage } from '@/lib/language-context';

export type Dictionary = Record<string, string>;

interface Pattern {
  expression: RegExp;
  /** The longest fixed piece of the template; cheap pre-check before running the expression. */
  anchor: string;
  template: string;
  /** Little fixed text, so the variable parts must look like values rather than free prose. */
  weak: boolean;
}

const ATTRIBUTES = ['placeholder', 'title', 'aria-label', 'alt'] as const;
const SKIPPED_TAGS = new Set(['SCRIPT', 'STYLE', 'CODE', 'PRE', 'TEXTAREA', 'NOSCRIPT']);
const loaders = import.meta.glob<{ default: Dictionary }>('./locales/*.json');

interface Applied {
  source: string;
  applied: string;
}

const textState = new WeakMap<Text, Applied>();
const attributeState = new WeakMap<Element, Partial<Record<string, Applied>>>();
const loaded = new Map<string, Dictionary>();

let dictionary: Dictionary = {};
let patterns: Pattern[] = [];
let cache = new Map<string, string>();
let current: AtlasLanguage = 'en';
let observer: MutationObserver | null = null;
let request = 0;

/** A number, amount or range, or up to four capitalised or numeric tokens such as "Pixel 10 Pro". */
const VALUE_LIKE = /^(?:[\d\s.,:%$€£×x/+-]+|[\p{Lu}\d][\p{L}\d._&'’-]*(?:\s[\p{Lu}\d(][\p{L}\d._&'’()-]*){0,3})$/u;

const escapeExpression = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

export function compilePatterns(entries: Dictionary): Pattern[] {
  return Object.entries(entries)
    .filter(([source]) => /\{\d+\}/.test(source))
    .map(([source, template]) => {
      const pieces = source.split(/\{\d+\}/);
      return {
        expression: new RegExp(
          '^' + source.split(/(\{\d+\})/).map((piece) => (/^\{\d+\}$/.test(piece) ? '(.+?)' : escapeExpression(piece))).join('') + '$',
          's',
        ),
        anchor: pieces.reduce((longest, piece) => (piece.length > longest.length ? piece : longest), ''),
        weak: (pieces.join('').match(/\p{L}/gu) ?? []).length < 12,
        template,
        order: [...source.matchAll(/\{(\d+)\}/g)].map((match) => Number(match[1])),
      };
    })
    // Prefer the most specific template when several could match.
    .sort((a, b) => b.anchor.length - a.anchor.length)
    .map(({ order, template, ...pattern }) => ({
      ...pattern,
      // Captures are positional; rewrite the template to refer to capture positions.
      template: template.replace(/\{(\d+)\}/g, (_match, index) => `{${order.indexOf(Number(index))}}`),
    }));
}

/** Translates one piece of English copy, keeping its surrounding whitespace. */
export function translateWith(entries: Dictionary, compiled: Pattern[], value: string, memo?: Map<string, string>): string {
  const known = memo?.get(value);
  if (known !== undefined) return known;
  const core = value.replace(/\s+/g, ' ').trim();
  let result = value;
  if (core && /\p{L}/u.test(core)) {
    const lead = value.slice(0, value.length - value.trimStart().length);
    const trail = value.slice(value.trimEnd().length);
    let translated = entries[core];
    if (translated === undefined && core.length <= 400) {
      for (const pattern of compiled) {
        if (pattern.anchor && !core.includes(pattern.anchor)) continue;
        const match = pattern.expression.exec(core);
        if (!match) continue;
        // Names, numbers and known labels may fill a template; sentences written by people or models may not.
        const plausible = match.slice(1).every((piece) => {
          const value = piece.trim();
          if (!pattern.weak) return value.length <= 80 && !value.includes('\n');
          return value.length <= 40 && (value in entries || VALUE_LIKE.test(value));
        });
        if (!plausible) continue;
        translated = pattern.template.replace(/\{(\d+)\}/g, (_match, index) => {
          const piece = match[Number(index) + 1] ?? '';
          return entries[piece.trim()] ?? piece;
        });
        break;
      }
    }
    if (translated !== undefined) result = lead + translated + trail;
  }
  if (memo) {
    if (memo.size > 5000) memo.clear();
    memo.set(value, result);
  }
  return result;
}

const translate = (value: string) => (current === 'en' ? value : translateWith(dictionary, patterns, value, cache));

function excluded(element: Element | null): boolean {
  for (let node = element; node; node = node.parentElement) {
    if (SKIPPED_TAGS.has(node.tagName.toUpperCase())) return true;
    if (node.getAttribute('translate') === 'no' || (node as HTMLElement).isContentEditable) return true;
  }
  return false;
}

function applyText(node: Text) {
  const value = node.nodeValue ?? '';
  const previous = textState.get(node);
  // If the text is still what this layer wrote, the app has not changed it since.
  const source = previous && previous.applied === value ? previous.source : value;
  const applied = translate(source);
  if (applied !== value) node.nodeValue = applied;
  textState.set(node, { source, applied });
}

function applyAttributes(element: Element) {
  for (const name of ATTRIBUTES) {
    const value = element.getAttribute(name);
    if (value === null) continue;
    const state = attributeState.get(element) ?? {};
    const previous = state[name];
    const source = previous && previous.applied === value ? previous.source : value;
    const applied = translate(source);
    if (applied !== value) element.setAttribute(name, applied);
    state[name] = { source, applied };
    attributeState.set(element, state);
  }
}

function applyTree(root: Node) {
  if (root.nodeType === Node.TEXT_NODE) {
    if (!excluded(root.parentElement)) applyText(root as Text);
    return;
  }
  if (root.nodeType !== Node.ELEMENT_NODE || excluded(root as Element)) return;
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT, {
    acceptNode: (node) => {
      if (node.nodeType === Node.TEXT_NODE) return NodeFilter.FILTER_ACCEPT;
      const element = node as Element;
      return SKIPPED_TAGS.has(element.tagName.toUpperCase()) || element.getAttribute('translate') === 'no' || (element as HTMLElement).isContentEditable
        ? NodeFilter.FILTER_REJECT
        : NodeFilter.FILTER_ACCEPT;
    },
  });
  applyAttributes(root as Element);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.nodeType === Node.TEXT_NODE) applyText(node as Text);
    else applyAttributes(node as Element);
  }
}

function observe() {
  if (observer || typeof MutationObserver === 'undefined') return;
  // Mutation callbacks run before the browser paints, so English copy is never shown first.
  observer = new MutationObserver((records) => {
    for (const record of records) {
      if (record.type === 'childList') record.addedNodes.forEach(applyTree);
      else if (record.type === 'characterData') applyTree(record.target);
      else if (record.target.nodeType === Node.ELEMENT_NODE && !excluded(record.target as Element)) applyAttributes(record.target as Element);
    }
  });
  observer.observe(document.body, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: [...ATTRIBUTES],
  });
}

async function load(language: AtlasLanguage): Promise<Dictionary> {
  if (language === 'en') return {};
  const cached = loaded.get(language);
  if (cached) return cached;
  const loader = loaders[`./locales/${language}.json`];
  const entries = loader ? (await loader()).default : {};
  loaded.set(language, entries);
  return entries;
}

/** Switches the whole rendered interface to a language, now and for everything rendered later. */
export async function applyLanguage(language: AtlasLanguage): Promise<void> {
  const ticket = ++request;
  const entries = await load(language).catch(() => ({}) as Dictionary);
  // A later switch won while this dictionary was loading.
  if (ticket !== request) return;
  dictionary = entries;
  patterns = compilePatterns(entries);
  cache = new Map();
  current = language;
  if (typeof document === 'undefined' || !document.body) return;
  observe();
  applyTree(document.body);
}
